import React, { useState, useEffect } from 'react';
import { useI3Theme } from '../context/I3ThemeContext';
import { VideoPlaceholder } from '../../../components/MediaPlaceholder';
import { lnAssets } from '../utils/lnAssets';
import { SITE_BRAND_SEAL_BOTTOM_ARC, SITE_BRAND_SEAL_TOP_ARC, SITE_FULL_NAME, SITE_HERO_TAGLINE } from '../sitePlaceholders';
import '../styles/I3Home.css';
import '../styles/I3IntroLanding.css';

/** After this delay, Bridge logo + welcome copy slide in */
const BRAND_REVEAL_DELAY_MS = 3000;
/** 3s hold + time to read logo + tagline before transition */
const WELCOME_DURATION_MS = 8000;
/** Fade centered seal lockup, then hand off to the mounted home shell */
const TRANSITION_FADE_MS = 900;

type Phase = 'welcome' | 'transition' | 'done';

type I3IntroLandingProps = {
  onComplete: () => void;
  isExiting?: boolean;
  /** True while intro fades — hides duplicate hero/welcome so the mounted shell reads cleanly */
  appShellVisible?: boolean;
};

const I3IntroLanding: React.FC<I3IntroLandingProps> = ({
  onComplete,
  isExiting = false,
  appShellVisible = false,
}) => {
  const { theme } = useI3Theme();
  const [phase, setPhase] = useState<Phase>('welcome');
  const [brandRevealed, setBrandRevealed] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setBrandRevealed(true), BRAND_REVEAL_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== 'welcome') return;
    const t = setTimeout(() => setPhase('transition'), WELCOME_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'transition') return;
    const doneTimer = setTimeout(() => {
      setPhase('done');
      onComplete();
    }, TRANSITION_FADE_MS);
    return () => clearTimeout(doneTimer);
  }, [phase, onComplete]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const inTransition = phase === 'transition' || phase === 'done';
  const introHandoff = phase === 'done' || isExiting;
  return (
    <div
      className={`i3-intro ${inTransition ? 'i3-intro--transition' : ''} ${isExiting ? 'i3-intro--exiting' : ''}${appShellVisible ? ' i3-intro--app-shell-visible' : ''}${introHandoff ? ' i3-intro--handoff' : ''} i3-intro--${theme}`}
    >
      <div className="i3-intro-backdrop" aria-hidden>
        <VideoPlaceholder
          className="i3-intro-backdrop-video"
          src={lnAssets.videos.landing}
          label="The Bridge — calm sunrise beach background"
          playbackRate={0.78}
        />
        <div className="i3-intro-backdrop-scrim i3-hero-backdrop-scrim--sunrise" aria-hidden />
      </div>
      <div className={`i3-intro-surface ${isExiting ? 'i3-intro-surface--exiting' : ''}`}>
        <div className={`i3-intro-welcome ${phase === 'welcome' ? 'i3-intro-welcome--active' : ''}`}>
          <div
            className={`i3-hero-content i3-intro-welcome-hero ${brandRevealed ? 'i3-intro-welcome-hero--revealed' : ''}`}
          >
            <div className="i3-intro-bridge-logo-wrap">
              <img
                src={lnAssets.logos.emblemLegacy}
                alt={SITE_FULL_NAME}
                className="i3-intro-bridge-logo"
                decoding="async"
              />
            </div>
            <div className="i3-intro-welcome-copy">
              <h1 className="i3-hero-headline">
                Welcome to {SITE_BRAND_SEAL_TOP_ARC}
              </h1>
              <p className="i3-intro-welcome-subtitle">{SITE_BRAND_SEAL_BOTTOM_ARC}</p>
            </div>
            <div
              className="i3-hero-tagline i3-hero-tagline--i3words"
              aria-label={SITE_HERO_TAGLINE.join(', ')}
            >
              {SITE_HERO_TAGLINE.map((word, i) => (
                <span key={word} className={`i3-tagline-i3word i3-tagline-i3word-${i + 1}`}>
                  {word}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default I3IntroLanding;
